import { HTTP } from '@utils/http';
import { clearPendingReferral } from 'hooks/useReferralContext';
import { createContext, useContext, useState } from 'react';
import { MindbodyUser } from 'types/client';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import { trackSignUp } from 'utils/tracking';

interface FirebaseUser {
  uid: string;
  email: string;
  mindbodyInfo: MindbodyUser;
}

interface LoginResponse {
  user: FirebaseUser;
  // ... autres propriétés renvoyées par l'endpoint
}

interface RegisterResponse {
  user: FirebaseUser;
  // ... autres propriétés renvoyées par l'endpoint
}

export interface RegisterData {
  FirstName: string;
  LastName: string;
  AddressLine1: string;
  City: string;
  PostalCode: string;
  Country: string;
  BirthDate: string;
  MobilePhone: string;
  Email: string;
  Password: string;
  ReferredBy: string;
  /** Case "infos réservations et planning" → SendAccountEmails + SendScheduleEmails côté serveur */
  ScheduleEmails: boolean;
  /** Case "actus, événements et offres" → SendPromotionalEmails côté serveur */
  PromotionalEmails: boolean;
  TermsAndConditions: boolean;

  /** Id Mindbody du parrain, tel qu'il est dans Mindbody : pas toujours numérique (`MSC444660`). */
  ReferralClientId?: string;
  /**
   * Id de la campagne de parrainage. Émis par le BO Parrainage, lu par
   * AuthService::register côté serveur pour valider la campagne (active,
   * dans la fenêtre temporelle, quotas non atteints) puis stocker
   * l'attribution. La relation Mindbody "Referred" reste créée même quand
   * la campagne est invalide → fallback legacy au post-purchase.
   */
  ReferralCampaignId?: string;

  CustomFields?: {
    ShoeSize?: number;
  };
}

interface IUserContext {
  isLoggedIn?: boolean;
  user?: MindbodyUser;
  setUser(user: MindbodyUser): void;
  isInitializing?: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (data: RegisterData) => Promise<void>;
}

interface UserProviderProps {
  children: React.ReactNode;
}

const UserContext = createContext({} as IUserContext);

function UserProvider({ children }: UserProviderProps) {
  const { user: mindbodyUser } = mondayMindbody ?? {}; // Si les infos en session, alors on set le défaut avec ce qu'on a dans le WP
  const [user, setUser] = useState<MindbodyUser | undefined>(mindbodyUser);

  const login = async (email: string, password: string) => {
    const data = await HTTP.post<LoginResponse>('/auth/login', {
      email,
      password,
    }).send();
    setUser(data.user.mindbodyInfo);
  };

  const logout = async (): Promise<void> => {
    try {
      await HTTP.post('/auth/logout').send();
      delete mondayMindbody.oauth?.user;
      // Poste partagé : un parrainage en attente ne doit pas suivre la
      // personne suivante.
      clearPendingReferral();

      // Open mindbody logout in another tab
      if (mondayMindbody.oauth.enabled) {
        const url = 'https://signin.mindbodyonline.com/connect/endsession';
        const tab = window.open(url, '_blank');
        if (tab) tab.focus();
      }

      setUser(undefined);
    } catch (error) {
      // Error already logged
    }
  };

  const signUp = async (data: RegisterData): Promise<void> => {
    console.log('[useUserContext] signUp called', {
      email: data.Email,
      firstName: data.FirstName,
      lastName: data.LastName,
      hasBirthDate: !!data.BirthDate,
      hasPhone: !!data.MobilePhone,
      hasReferralId: !!data.ReferralClientId,
    });

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'SignUp API call initiated',
      level: 'info',
      data: {
        email: data.Email,
        hasReferralId: !!data.ReferralClientId,
      },
    });

    try {
      const response = await HTTP.post<RegisterResponse>('/auth/register', data).send();
      console.log('[useUserContext] signUp response received', {
        userId: response.user?.mindbodyInfo?.Id,
        email: response.user?.mindbodyInfo?.Email,
        fullResponse: response,
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'SignUp API call successful',
        level: 'info',
        data: {
          userId: response.user?.mindbodyInfo?.Id,
          email: response.user?.mindbodyInfo?.Email,
        },
      });

      // Breadcrumb et non event : une inscription réussie n'est pas une erreur
      // (276 events en deux jours). Sans l'e-mail : PII, et le breadcrumb
      // accompagne tout event suivant de la session.
      sentryTrace('User registration successful', {
        tags: {
          flow: 'oauth',
          step: 'signup_api_success',
        },
        extra: {
          userId: response.user?.mindbodyInfo?.Id,
        },
      });

      setUser(response.user.mindbodyInfo);

      // GA4 sign_up : compte créé avec succès.
      trackSignUp('mindbody');
    } catch (error: any) {
      console.error('[useUserContext] signUp error', {
        error: error?.message || error,
        errorType: error?.constructor?.name,
      });

      Sentry.captureException(error, {
        tags: {
          flow: 'oauth',
          step: 'signup_api_error',
        },
        extra: {
          email: data.Email,
          errorMessage: error?.message || String(error),
        },
      });

      throw error;
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn: !!user,
        login,
        logout,
        signUp,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

function useUserContext() {
  const context = useContext(UserContext);
  if (context === undefined)
    throw new Error('You must wrap your React tree with <UserProvider /> in order to use useUserContext()');
  return context;
}

export { UserProvider, useUserContext };
